import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { adminService } from '../../../features/admins/services/adminService';
import Modal from '../../common/Modal';
import { XMarkIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const PasswordChangeModal = ({ isOpen, onClose, user }) => {
  const [formData, setFormData] = useState({
    old_password: '',
    password: '',
    password_confirmation: ''
  });

  const [errors, setErrors] = useState({});
  const [showPasswords, setShowPasswords] = useState({
    old_password: false,
    password: false,
    password_confirmation: false
  });

  const changePasswordMutation = useMutation({
    mutationFn: (data) => adminService.resetAdminPassword(user.id, data),
    onSuccess: () => {
      toast.success('Mot de passe modifié avec succès');
      onClose();
      // Reset form
      setFormData({
        old_password: '',
        password: '',
        password_confirmation: ''
      });
      setErrors({});
    },
    onError: (error) => {
      toast.error('Erreur lors de la modification du mot de passe');
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else if (error.response?.data?.message) {
        setErrors({ general: error.response.data.message });
      }
    }
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.old_password) {
      newErrors.old_password = 'L\'ancien mot de passe est obligatoire';
    }

    if (!formData.password) {
      newErrors.password = 'Le nouveau mot de passe est obligatoire';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Le mot de passe doit contenir au moins 8 caractères';
    }

    if (!formData.password_confirmation) {
      newErrors.password_confirmation = 'La confirmation est obligatoire';
    } else if (formData.password !== formData.password_confirmation) {
      newErrors.password_confirmation = 'Les mots de passe ne correspondent pas';
    }

    if (formData.old_password === formData.password) {
      newErrors.password = 'Le nouveau mot de passe doit être différent de l\'ancien';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    changePasswordMutation.mutate(formData);
  };

  const isLoading = changePasswordMutation.isPending;

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="w-full ">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold text-gray-900">
            Changer mon mot de passe
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {errors.general && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800">{errors.general}</p>
            </div>
          )}

          {/* Ancien mot de passe */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
              Ancien mot de passe *
            </label>
            <div className="relative">
              <input
                type={showPasswords.old_password ? 'text' : 'password'}
                name="old_password"
                value={formData.old_password}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 pr-12 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors ${
                  errors.old_password ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
                placeholder="Saisissez votre ancien mot de passe"
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('old_password')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPasswords.old_password ? (
                  <EyeSlashIcon className="h-5 w-5" />
                ) : (
                  <EyeIcon className="h-5 w-5" />
                )}
              </button>
            </div>
            {errors.old_password && (
              <p className="mt-1 text-sm text-red-600 text-left">{errors.old_password}</p>
            )}
          </div>

          {/* Nouveau mot de passe */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
              Nouveau mot de passe *
            </label>
            <div className="relative">
              <input
                type={showPasswords.password ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 pr-12 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors ${
                  errors.password ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
                placeholder="Saisissez votre nouveau mot de passe"
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('password')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPasswords.password ? (
                  <EyeSlashIcon className="h-5 w-5" />
                ) : (
                  <EyeIcon className="h-5 w-5" />
                )}
              </button>
            </div>
            {errors.password && (
              <p className="mt-1 text-sm text-red-600 text-left">{errors.password}</p>
            )}
          </div>

          {/* Confirmer nouveau mot de passe */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
              Confirmer le nouveau mot de passe *
            </label>
            <div className="relative">
              <input
                type={showPasswords.password_confirmation ? 'text' : 'password'}
                name="password_confirmation"
                value={formData.password_confirmation}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 pr-12 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors ${
                  errors.password_confirmation ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
                placeholder="Confirmez votre nouveau mot de passe"
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('password_confirmation')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPasswords.password_confirmation ? (
                  <EyeSlashIcon className="h-5 w-5" />
                ) : (
                  <EyeIcon className="h-5 w-5" />
                )}
              </button>
            </div>
            {errors.password_confirmation && (
              <p className="mt-1 text-sm text-red-600 text-left">{errors.password_confirmation}</p>
            )}
          </div>

          {/* Conseils de sécurité */}
         
          <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-6 py-3 text-sm font-medium text-white bg-primary border border-transparent rounded-lg hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? 'En cours...' : 'Changer le mot de passe'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default PasswordChangeModal;